import gsap from "gsap";
import Experience from "../Experience";
import { episodes } from "../sources";
import EpisodeSliderGL from "./EpisodeSliderGL";
import TcoPhase2NotifyFlip from "./TcoPhase2NotifyFlip";

// Card size (Figma node 2290:1577 "Episodes", Card 0-3: 720x405).
const CARD_WIDTH = 720;
const CARD_HEIGHT = 405;
const CARD_GAP = 24; // matches the row's existing gap value
const SLOT_PITCH = CARD_WIDTH + CARD_GAP;

// Reference: SWIPE_COMMIT_PX = 40 — net pointer travel past this commits a
// slide immediately. There is NO 1:1 visual drag-follow in the reference:
// the track position is driven entirely by the scripted snap tween
// (settleTrack), never by raw pointer delta — a drag gesture only ever
// decides direction/commit, the card position itself always animates via
// gsap, matching the reference's own snapToIndex model exactly.
const DRAG_COMMIT_THRESHOLD = 40;
const SNAP_DURATION = 0.35; // reference calls this SLIDE_DURATION; also reused as the bg wipe's own duration

// Reference (hyundai-sa-fe TcoPhase2WebGlSlider.ts) constants — raw px/s
// velocity is scaled directly into the shader's uVelocity range, smoothed
// with an asymmetric attack/release (snappier response to new motion,
// slower settle) rather than one fixed lerp factor.
const WARP_SCALE = 0.0009;
const WARP_ATTACK = 0.28;
const WARP_RELEASE = 0.08;
const VELOCITY_DECAY_TIME = 0.15; // seconds to fully settle once stationary

// Background clip-path wipe (port of the reference's syncBackground).
const BG_DURATION = SNAP_DURATION;
const BG_REVEAL_SCALE = 1.28;
const BG_SCALE_DURATION = SNAP_DURATION * 1.6;

// Title/caption slide-change transition (plain two-leg crossfade, NOT the
// reference's SplitText letter/word/line staggering — dropped per "not
// overengineered").
const TEXT_OUT_DURATION = 0.25;
const TEXT_IN_DURATION = 0.25;
const TEXT_TRAVEL_Y = 12; // caption's own vertical travel
const TEXT_TRAVEL_X = 60; // title's own horizontal travel (reference: TITLE_TRAVEL_X)

// Quiz-pill <-> notify-form swap (reference: BOTTOM_CHROME_SWAP_S/TRAVEL_Y).
const CHROME_SWAP_DURATION = 0.4;
const CHROME_TRAVEL_Y = 12;

// Post-baked-timeline finale UI — a 6-episode picker. Faithful port of the
// real reference (hyundai-sa-fe's TcoPhase2Slider.tsx/TcoPhase2WebGlSlider.ts
// /tcoSliderCardMaterial.ts), rebuilt on this codebase's own simpler stack
// (classic ShaderMaterial/GLSL, plain gsap, hand-written CSS) instead of
// its WebGPU/TSL/React/Formik machinery — same visual result, less
// machinery. "HTML over WebGL": this class owns the real DOM (layout,
// input, hit-testing), EpisodeSliderGL owns the actual card visuals as a
// second WebGL scene/camera rendered on top of the main canvas (see
// Renderer.js) but bypassing its grade/FXAA pipeline.
//
// Built hidden (opacity 0) at construction time and populated with slide
// 0's content — safe to mount immediately since EndCurtain's opaque
// panels cover the whole viewport until they open. show() (called before
// the curtain opens) makes the root + background visible; reveal()
// (called once the curtain finishes opening) plays the zoom/blur
// choreography, fades the UI in, and starts the WebGL layer (see
// EpisodeSliderGL.setVisible).
export default class EpisodeSlider {
  constructor() {
    this.experience = new Experience();

    this.episodes = episodes;
    this.activeIndex = 0;
    this.navLocked = false;

    this.dragging = false;
    this.dragStartX = 0;
    this.trackX = 0;
    this.prevTrackX = 0;
    this.velocity = 0;

    // Background clip-path wipe stack (port of the reference's
    // bgLayersRef/bgCurrentSrcRef) — see syncBackground().
    this.bgLayers = [];
    this.bgCurrentSrc = null;

    this.buildDom();
    this.setInputListeners();

    this.notifyFlip = new TcoPhase2NotifyFlip();

    // Populates title/caption/background for the initial slide — must
    // happen before anything is shown (buildCard() only sets each card's
    // OWN poster <img>, never the separate background/title/caption/
    // chrome content).
    this.updateContent(this.activeIndex, { instant: true });
    this.syncChrome(this.activeIndex, { instant: true });

    this.gl = new EpisodeSliderGL(this.experience);
    this.setTrackX(this.targetTrackX(this.activeIndex), { instant: true });
  }

  // Makes the root (and its zoomed-in, sharp background image) visible —
  // called from World.triggerFinale() BEFORE the curtain opens, while it
  // still fully covers the screen. Deliberately separate from reveal():
  // without it, the curtain would part to expose whatever the WebGL
  // canvas underneath still shows (the old scene's last frame), not this
  // slider, since the root sits at opacity 0 from construction until
  // something explicitly turns it on. Pointer events (and the WebGL
  // card layer) stay off until reveal() — no interaction while still
  // mid-transition.
  show() {
    this.root.style.opacity = "1";
    gsap.set(this.bgFrontImg, { scale: 1.12, filter: "blur(0px)" });
  }

  buildDom() {
    const root = document.createElement("div");
    root.className = "episode-slider";

    // Blurred backdrop: a base plate (bgFront) plus a host (bgStack) for
    // in-flight clip-path wipe layers appended on each slide change — see
    // syncBackground(). Matches the reference's bgFrontRef/bgStackRef.
    const bgWrap = document.createElement("div");
    bgWrap.className = "es-bg";

    this.bgFront = document.createElement("div");
    this.bgFront.className = "es-bg-layer is-front";
    this.bgFrontImg = document.createElement("img");
    this.bgFrontImg.className = "es-bg-layer-img";
    this.bgFront.appendChild(this.bgFrontImg);

    this.bgStack = document.createElement("div");
    this.bgStack.className = "es-bg-stack";

    bgWrap.append(this.bgFront, this.bgStack);

    const scrim = document.createElement("div");
    scrim.className = "es-bg-scrim";

    const ui = document.createElement("div");
    ui.className = "es-ui";
    // Stays hidden until reveal()'s UI-fade-in step — the root itself
    // becomes visible earlier (see show()), while the curtain is still
    // covering it, but the title/cards/caption/chrome shouldn't be
    // visible yet at that point, only the zoomed background image.
    ui.style.opacity = "0";

    this.titleEl = document.createElement("p");
    this.titleEl.className = "es-title";

    const viewport = document.createElement("div");
    viewport.className = "es-viewport";

    const track = document.createElement("div");
    track.className = "es-track";

    // All 6 cards exist simultaneously (not recycled slots) — each one
    // keeps a permanent 1:1 WebGL plane (see EpisodeSliderGL), and
    // .es-viewport's overflow:hidden clips the track to a 3-card-wide
    // window that .es-track's translateX scrolls through.
    this.cards = this.episodes.map((episode) => this.buildCard(episode));
    track.append(...this.cards.map((card) => card.root));
    viewport.appendChild(track);

    this.captionEl = document.createElement("p");
    this.captionEl.className = "es-caption";

    // Bottom chrome: quiz pill (streamed episodes) and the notify-flip
    // form (non-streamed) occupy the same slot, cross-swapped by
    // syncChrome() — see the reference's revealEntranceChrome/runtime swap.
    const quizPill = document.createElement("a");
    quizPill.className = "es-quiz-pill";
    quizPill.textContent = "TAKE THE QUIZ";
    this.quizPill = quizPill;

    // Small cursor-following drag hint — replaces click-arrow buttons
    // entirely (confirmed against the reference: there are no clickable
    // arrows, navigation is drag/wheel/keyboard only).
    const cursorPill = document.createElement("div");
    cursorPill.className = "es-cursor-pill";
    cursorPill.innerHTML = "<span>&lsaquo;</span><i></i><span>&rsaquo;</span>";
    this.cursorPill = cursorPill;

    ui.append(this.titleEl, viewport, this.captionEl, quizPill);

    root.append(bgWrap, scrim, ui, cursorPill);
    document.body.appendChild(root);

    this.root = root;
    this.ui = ui;
    this.viewport = viewport;
    this.track = track;
  }

  // One permanent card slot — its own <img> poster (the WebGL plane owns
  // the actual video texture; this <img>/media div stays in the DOM for
  // layout/hit-testing but is painted invisible, see style.css) plus a
  // tag label, matching the Figma card composition.
  buildCard(episode) {
    const root = document.createElement("div");
    root.className = "es-card";
    Object.assign(root.style, {
      width: `${CARD_WIDTH}px`,
      height: `${CARD_HEIGHT}px`,
    });

    const media = document.createElement("div");
    media.className = "es-card-media";

    const img = document.createElement("img");
    img.className = "es-card-img";
    img.src = episode.image;

    media.appendChild(img);

    const tag = document.createElement("span");
    tag.className = "es-card-tag";
    tag.textContent = episode.tag;

    root.append(media, tag);

    return { root, media, img, tag };
  }

  // Target .es-track translateX (px) that centers the given index's card
  // inside .es-viewport (a 3-card-wide window).
  targetTrackX(index) {
    return -index * SLOT_PITCH;
  }

  setTrackX(x, { instant = false } = {}) {
    this.trackX = x;
    if (instant) gsap.set(this.track, { x });
  }

  // Background clip-path wipe — direct port of the reference's
  // syncBackground/setBgImage. `immediate` (used for the very first mount
  // and any hard cut) drops in-flight wipes and snaps bgFront straight to
  // the target image; otherwise a new layer is stacked above bgStack and
  // wiped in from the edge the incoming card travels from, so a fast
  // scroll leaves several wipes overlapping rather than one cancelling
  // the next.
  setBgImage(layer, src) {
    const img = layer.querySelector("img");
    if (!img || img.src === src) return;
    img.src = src;
  }

  syncBackground(index, { instant = false, previousIndex = index } = {}) {
    const slide = this.episodes[index];

    if (instant) {
      this.bgLayers.forEach(({ layer, timeline }) => {
        timeline.kill();
        layer.remove();
      });
      this.bgLayers = [];
      this.bgCurrentSrc = slide.image;
      this.setBgImage(this.bgFront, slide.image);
      gsap.set(this.bgFront, { autoAlpha: 1, scale: 1, clipPath: "none" });
      return;
    }

    if (this.bgCurrentSrc === slide.image) return;
    this.bgCurrentSrc = slide.image;

    const layer = document.createElement("div");
    layer.className = "es-bg-layer";
    const img = document.createElement("img");
    img.className = "es-bg-layer-img";
    img.src = slide.image;
    layer.appendChild(img);
    this.bgStack.appendChild(layer);

    const forward = index > previousIndex;
    const from = forward ? "inset(0% 0% 0% 100%)" : "inset(0% 100% 0% 0%)";

    const entry = {
      layer,
      timeline: gsap.timeline({
        onComplete: () => {
          this.setBgImage(this.bgFront, slide.image);
          gsap.set(this.bgFront, { autoAlpha: 1, scale: 1, clipPath: "none" });
          gsap.set(this.bgFrontImg, { scale: 1 });
          const at = this.bgLayers.indexOf(entry);
          this.bgLayers.slice(0, at + 1).forEach(({ layer: done }) => done.remove());
          this.bgLayers = this.bgLayers.slice(at + 1);
        },
      }),
    };
    this.bgLayers.push(entry);

    entry.timeline
      .fromTo(layer, { clipPath: from }, { clipPath: "inset(0% 0% 0% 0%)", duration: BG_DURATION, ease: "none" }, 0)
      .fromTo(img, { scale: BG_REVEAL_SCALE }, { scale: 1, duration: BG_SCALE_DURATION, ease: "power2.out" }, 0);
  }

  // Title/caption text swap on slide change — a plain two-leg crossfade
  // (out -> swap text -> in), not the reference's per-letter/per-line
  // SplitText staggering, but matching its two DIFFERENT axes: the title
  // travels HORIZONTALLY, in the direction of navigation (reference:
  // words slide out toward the direction of travel, then the new title
  // enters mirrored from the opposite side); the caption travels
  // VERTICALLY (reference: description lines push up/down). Using one
  // shared axis for both (an earlier version of this) didn't match either.
  updateContent(index, { instant = false, previousIndex = index } = {}) {
    const active = this.episodes[index];
    const forward = index > previousIndex;

    const apply = () => {
      this.titleEl.textContent = active.title;
      this.captionEl.textContent = active.caption;
    };

    this.syncBackground(index, { instant, previousIndex });

    if (instant) {
      apply();
      return;
    }

    const titleOutX = forward ? -TEXT_TRAVEL_X : TEXT_TRAVEL_X;
    const titleInX = forward ? TEXT_TRAVEL_X : -TEXT_TRAVEL_X;

    const tl = gsap.timeline();
    tl.to(this.titleEl, { opacity: 0, x: titleOutX, duration: TEXT_OUT_DURATION, ease: "power2.in" }, 0);
    tl.to(this.captionEl, { opacity: 0, y: -TEXT_TRAVEL_Y, duration: TEXT_OUT_DURATION, ease: "power2.in" }, 0);
    tl.add(apply);
    tl.fromTo(this.titleEl, { opacity: 0, x: titleInX }, { opacity: 1, x: 0, duration: TEXT_IN_DURATION, ease: "power3.out" }, ">");
    tl.fromTo(this.captionEl, { opacity: 0, y: TEXT_TRAVEL_Y }, { opacity: 1, y: 0, duration: TEXT_IN_DURATION, ease: "power2.out" }, "<");
  }

  // Swaps the bottom chrome between the quiz pill (isStreamed episodes)
  // and the notify-flip form (upcoming episodes) — port of the
  // reference's revealEntranceChrome/runtime cue<->notify swap.
  syncChrome(index, { instant = false } = {}) {
    const slide = this.episodes[index];

    if (instant) {
      if (slide.isStreamed) {
        gsap.set(this.quizPill, { autoAlpha: 1, y: 0 });
        this.notifyFlip.hide();
        gsap.set(this.notifyFlip.root, { autoAlpha: 0 });
      } else {
        gsap.set(this.quizPill, { autoAlpha: 0, y: 0 });
        this.notifyFlip.show(slide.streamingLabel);
      }
      return;
    }

    const tl = gsap.timeline({ defaults: { duration: CHROME_SWAP_DURATION, ease: "power2.inOut" } });
    if (slide.isStreamed) {
      tl.to(this.notifyFlip.root, { autoAlpha: 0, y: CHROME_TRAVEL_Y });
      tl.add(() => this.notifyFlip.hide());
      tl.fromTo(this.quizPill, { autoAlpha: 0, y: -CHROME_TRAVEL_Y }, { autoAlpha: 1, y: 0 });
    } else {
      tl.to(this.quizPill, { autoAlpha: 0, y: -CHROME_TRAVEL_Y });
      tl.add(() => this.notifyFlip.show(slide.streamingLabel));
      tl.fromTo(this.notifyFlip.root, { autoAlpha: 0, y: CHROME_TRAVEL_Y }, { autoAlpha: 1, y: 0 });
    }
  }

  prev() {
    this.goTo(this.activeIndex - 1);
  }

  next() {
    this.goTo(this.activeIndex + 1);
  }

  goTo(index) {
    const clamped = Math.max(0, Math.min(this.episodes.length - 1, index));
    if (clamped === this.activeIndex || this.navLocked) {
      // Not actually moving — still settle the track back to its current
      // target (covers a drag that didn't clear the commit threshold).
      this.settleTrack();
      return;
    }

    const previousIndex = this.activeIndex;
    this.activeIndex = clamped;
    this.navLocked = true;
    this.updateContent(this.activeIndex, { previousIndex });
    this.syncChrome(this.activeIndex);
    this.gl.setActive(this.activeIndex);
    this.settleTrack();

    gsap.delayedCall(SNAP_DURATION, () => {
      this.navLocked = false;
    });
  }

  settleTrack() {
    const target = this.targetTrackX(this.activeIndex);
    gsap.killTweensOf(this.track);
    gsap.to(this.track, {
      x: target,
      duration: SNAP_DURATION,
      ease: "power3.out",
      onUpdate: () => {
        this.trackX = gsap.getProperty(this.track, "x");
      },
    });
  }

  setInputListeners() {
    window.addEventListener("keydown", (event) => {
      if (this.root.style.pointerEvents === "none") return;
      if (event.key === "ArrowLeft") this.prev();
      if (event.key === "ArrowRight") this.next();
    });

    this.viewport.addEventListener("wheel", (event) => {
      event.preventDefault();
      if (event.deltaY > 0 || event.deltaX > 0) this.next();
      else this.prev();
    }, { passive: false });

    // No visual drag-follow — matches the reference exactly: the track's
    // on-screen position is ALWAYS driven by the scripted snap tween
    // (settleTrack), never by raw pointer delta. A drag/swipe gesture only
    // measures net travel to decide whether to commit a step; it never
    // moves the cards itself while in progress.
    const onDragStart = (clientX) => {
      this.dragging = true;
      this.dragStartX = clientX;
    };
    const onDragEnd = (clientX) => {
      if (!this.dragging) return;
      this.dragging = false;
      const deltaX = clientX - this.dragStartX;
      this.commitDrag(deltaX);
    };

    this.viewport.addEventListener("touchstart", (event) => {
      onDragStart(event.touches[0].clientX);
    }, { passive: true });
    this.viewport.addEventListener("touchend", (event) => {
      onDragEnd(event.changedTouches[0].clientX);
    }, { passive: true });

    this.viewport.addEventListener("mousedown", (event) => {
      onDragStart(event.clientX);
    });
    // On window, not just viewport: a release outside the viewport (drag
    // that ends past its edge) must still end the gesture — without this
    // `dragging` could stay stuck true, needing a second click to "let go"
    // (the bug reported earlier), since a viewport-scoped listener alone
    // never fires once the pointer has left it.
    window.addEventListener("mouseup", (event) => {
      onDragEnd(event.clientX);
    });

    // Cursor-follow drag hint — purely visual, doesn't itself navigate.
    this.viewport.addEventListener("mouseenter", () => {
      gsap.to(this.cursorPill, { opacity: 1, duration: 0.2 });
    });
    this.viewport.addEventListener("mouseleave", () => {
      gsap.to(this.cursorPill, { opacity: 0, duration: 0.2 });
    });
    this.viewport.addEventListener("mousemove", (event) => {
      this.cursorPill.style.transform = `translate(${event.clientX}px, ${event.clientY}px) translate(-50%, -50%)`;
    });
  }

  // Net drag distance beyond DRAG_COMMIT_THRESHOLD (reference:
  // SWIPE_COMMIT_PX = 40) commits a step in that direction. Otherwise the
  // track just settles back to the current activeIndex (a no-op visually,
  // since it was never moved in the first place — see setInputListeners).
  // Never skips more than one card per gesture.
  commitDrag(deltaX) {
    if (Math.abs(deltaX) <= DRAG_COMMIT_THRESHOLD) {
      this.settleTrack();
      return;
    }

    // Dragging left (negative deltaX) reveals the NEXT card; dragging
    // right reveals the PREVIOUS one.
    if (deltaX < 0) this.next();
    else this.prev();
  }

  // First choreography step — zooms the (already-visible, see show())
  // background image out slightly. Called at the same moment as
  // EndCurtain.open() starts, with the same duration, so the zoom-out
  // plays WHILE the curtain is parting rather than only starting once it
  // finishes. This is the ONE-TIME curtain-reveal intro treatment on
  // bgFront specifically — separate from syncBackground()'s per-slide-
  // change wipes, exactly mirroring how the reference seeds bgFront once
  // via syncBackground(initialIndex, true) before its own separate intro
  // plays.
  beginZoomOut(duration) {
    gsap.to(this.bgFrontImg, {
      scale: 1.0,
      duration,
      ease: "power3.inOut",
    });
  }

  // Zoom-in-while-blurring / UI-fade-in choreography, played once
  // EndCurtain's open() (and beginZoomOut's matching zoom-out) finishes.
  // Also where the WebGL card layer itself first becomes visible and
  // starts the active card's video — see EpisodeSliderGL.setVisible.
  reveal() {
    this.root.style.pointerEvents = "auto";
    this.gl.setVisible(true, this.activeIndex);

    const tl = gsap.timeline();
    tl.to(this.bgFrontImg, {
      scale: 1.08,
      filter: "blur(24px)",
      duration: 0.8,
      ease: "power2.inOut",
    });
    tl.fromTo(
      this.ui,
      { opacity: 0 },
      { opacity: 1, duration: 0.6, ease: "power2.out" },
      "-=0.2",
    );
  }

  update(dt) {
    // Track velocity from the resolved track position each tick — works
    // uniformly whether the motion is drag-follow, a gsap settle tween,
    // or nothing at all (idle). Reference's own asymmetric attack/release:
    // snappier response to new motion, slower settle, rather than one
    // fixed lerp factor.
    const rawVelocity = dt > 0 ? (this.trackX - this.prevTrackX) / dt : 0;
    this.prevTrackX = this.trackX;

    const raw = rawVelocity * WARP_SCALE;
    const smoothing = Math.abs(raw) > Math.abs(this.velocity) ? WARP_ATTACK : WARP_RELEASE;
    this.velocity += (raw - this.velocity) * smoothing;

    if (Math.abs(raw) < 0.001 && !this.dragging) {
      this.velocity *= Math.pow(0.001, dt / VELOCITY_DECAY_TIME);
    }

    this.gl.setVelocity(this.velocity);
    this.gl.sync(this.cards.map((card) => card.root));
  }

  resize() {
    this.gl.resize(this.experience.sizes.width, this.experience.sizes.height);
  }
}
